// CallVolumeTile.js — daily call count line chart over the window.
// Data: collectionGroup('call_history') filtered by timestamp >= start.

import React from 'react';
import { Card, CardContent, Typography, Box, IconButton, Tooltip as MuiTooltip } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { useCallVolumeSeries, toCSV, downloadCSV } from '../../../hooks/useAdminMetrics';

const CallVolumeTile = ({ days = 30 }) => {
  const { series, total, loading, error } = useCallVolumeSeries(days);

  const handleCSV = () => {
    downloadCSV(
      `call-volume-${days}d.csv`,
      toCSV(series, [
        { key: 'date', header: 'date' },
        { key: 'count', header: 'calls' },
      ]),
    );
  };

  if (error) {
    return (
      <Card><CardContent>
        <Typography color="error">Call volume unavailable: {error.message}</Typography>
      </CardContent></Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" gutterBottom>Call Volume ({days}d)</Typography>
          <MuiTooltip title="Download CSV">
            <span>
              <IconButton size="small" onClick={handleCSV} disabled={loading || series.length === 0}>
                <DownloadIcon fontSize="small" />
              </IconButton>
            </span>
          </MuiTooltip>
        </Box>
        <Typography variant="h4" gutterBottom>
          {loading ? '…' : total.toLocaleString()}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          total calls in window
        </Typography>
        <Box sx={{ width: '100%', height: 220 }}>
          {loading ? null : (
            <ResponsiveContainer>
              <LineChart data={series}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Line type="monotone" dataKey="count" stroke="#388e3c" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          Phone calls handled by Maia per day. One call_history record = one call (inbound or outbound).
        </Typography>
      </CardContent>
    </Card>
  );
};

export default CallVolumeTile;
